import { DataTable, createDataTableColumnHelper } from '@ridehub/ui-widgets';
import React, { useState } from 'react';

import { Row } from '@tanstack/react-table';
import { Skeleton } from '@flixbus/honeycomb-react';
import { useIntervalEffect } from '@react-hookz/web';
import { useLocalization } from '@ridehub/feat-localization';

interface TableSkeletonProps {
    rowCount: number;
}

// Generate union type of listed array values
const SKELETON_ROW_KEYS = [
    'lineCode',
    'tripNumber',
    'route',
    'departure',
    'yield',
    'eurKm',
    'demandLoad',
    'maxLoad',
    'bidPrice',
    'details',
] as const;
type SkeletonRowKey = (typeof SKELETON_ROW_KEYS)[number];
type SkeletonRow = {
    [key in SkeletonRowKey]: boolean;
};

export function TableSkeleton(props: TableSkeletonProps) {
    const [skeletonAnimationStep, setSkeletonAnimationStep] = useState(0);
    const animationStepMs: number | undefined = 200;
    useIntervalEffect(() => {
        setSkeletonAnimationStep(skeletonAnimationStep + 1);
    }, animationStepMs);
    const localization = useLocalization();

    const rows: Array<SkeletonRow> = Array(props.rowCount)
        .fill(undefined)
        .map((_, ridx) => {
            const keys: Array<keyof SkeletonRow> = [...SKELETON_ROW_KEYS];
            const highlightedKey = keys[skeletonAnimationStep % keys.length];
            const row = {} as SkeletonRow;
            keys.forEach((key) => {
                row[key] = key === highlightedKey;
            });
            return row;
        });
    const { accessor, group } = createDataTableColumnHelper<(typeof rows)[0]>();
    function skeletonCell(cellName: SkeletonRowKey, row: Row<SkeletonRow>) {
        if (row.original[cellName]) {
            return <Skeleton width="md" style={{ marginLeft: 0 }} />;
        } else {
            return null;
        }
    }
    const columns = [
        group({
            header: 'Info',
            columns: [
                accessor('lineCode', {
                    header: 'Line Code',
                    size: 30,
                    cell: ({ row }) => skeletonCell('lineCode', row),
                }),
                accessor('tripNumber', {
                    header: 'TN',
                    maxSize: 30,
                    cell: ({ row }) => skeletonCell('tripNumber', row),
                }),
                accessor('route', {
                    header: 'Route',
                    size: 900,
                    cell: ({ row }) => skeletonCell('route', row),
                }),
                accessor('departure', {
                    header: 'Departure',
                    size: 600,
                    cell: ({ row }) => skeletonCell('departure', row),
                }),
            ],
        }),
        group({
            header: 'Commercial',
            columns: [
                accessor('yield', { header: 'Yield', size: 300, cell: ({ row }) => skeletonCell('yield', row) }),
                accessor('eurKm', {
                    header: localization.localizeUnit('revenuePerDistance', false, 'report'),
                    size: 400,
                    cell: ({ row }) => skeletonCell('eurKm', row),
                }),
            ],
        }),
        group({
            header: 'Load',
            columns: [
                accessor('demandLoad', {
                    header: 'Demand',
                    size: 30,
                    cell: ({ row }) => skeletonCell('demandLoad', row),
                }),
                accessor('maxLoad', { header: 'Max', size: 30, cell: ({ row }) => skeletonCell('maxLoad', row) }),
                accessor('bidPrice', {
                    header: 'Bid Price',
                    size: 30,
                    cell: ({ row }) => skeletonCell('bidPrice', row),
                }),
            ],
        }),
        group({
            header: 'Actions',
            columns: [
                accessor('details', {
                    header: 'Details',
                    cell: ({ row }) => skeletonCell('details', row),
                }),
            ],
        }),
    ];
    return <DataTable id={'table-skeleton'} scrollable={true} columns={columns} data={rows} />;
}
